/**
 * VD — личный подбор (режим hide/warn/ignore, «может содержать», подтверждение по SKU) и
 * вспомогательное состояние КБЖУ-витрины (защита от устаревших ответов, сравнение 2–3).
 *
 * Источник истины — сервер. Store не меняет режим оптимистично и не решает сам, можно ли
 * добавить товар: решение и ключ предупреждения приходят из /personal-selection/evaluate,
 * подтверждение уходит на /confirm с серверным ключом, поколением и ревизией.
 * Подписи ограничений (PII профиля) сюда не попадают — их передаёт страница.
 */
import { defineStore } from "pinia";
import { StorefrontApiError } from "@/api/storefront";
import {
  personalSelectionApi,
  selectionErrorCode,
  type PersonalSelectionMode,
  type PersonalSelectionSnapshot,
  type PersonalSelectionWriteOk,
  type SelectionErrorCode,
  type SelectionReason,
} from "@/api/nutrition";

/** feature_off — сервер явно сообщил, что личный подбор не настроен: можно обычное добавление. */
export type CheckOutcome = "allow" | "blocked" | "cancelled" | "stale" | "error" | "feature_off";

export interface PendingConfirmation {
  itemId: string;
  title: string;
  reasons: SelectionReason[];
  warningKey: string;
  expectedGeneration: number;
  expectedRevision: number;
}

export interface CompareEntry { id: string; basisKey: string }
export type CompareToggle = "added" | "removed" | "full" | "mixed_basis";
export const COMPARE_MAX = 3;

interface State {
  session: PersonalSelectionSnapshot | null;
  status: "idle" | "loading" | "ready" | "error" | "off";
  pendingMode: PersonalSelectionMode | null;
  pendingConfirmation: PendingConfirmation | null;
  busy: boolean;
  message: SelectionErrorCode | null;
  compare: CompareEntry[];
}

/**
 * Отказ корзины, который может быть связан с личным подбором: 422 без иного известного кода.
 * Ключ подтверждения из тела корзины НЕ используется — только как сигнал к серверной перепроверке.
 */
export function isSelectionCartRejection(err: unknown): boolean {
  if (!(err instanceof StorefrontApiError) || err.statusCode !== 422) return false;
  const p = err.payload && typeof err.payload === "object" ? err.payload as Record<string, unknown> : {};
  const d = p.details && typeof p.details === "object" ? p.details as Record<string, unknown> : {};
  if (p.decision !== undefined || p.confirmationRequired !== undefined || d.decision !== undefined || d.confirmationRequired !== undefined) return true;
  if (typeof p.error === "string" && /SELECTION|ADMISSION/i.test(p.error)) return true;
  // Тело ошибки Fastify по умолчанию (CartAdmissionBlockedError): только statusCode/error/message.
  return p.code === undefined && (p.error === undefined || p.error === "Unprocessable Entity");
}

// Private async state belongs to one store instance, never shared between buyers/Pinia roots.
interface Runtime {
  epoch: number;
  resolveConfirmation: ((answer: boolean) => void) | null;
  requestSeq: Map<string, number>;
}
const runtimes = new WeakMap<object, Runtime>();
function runtime(store: object): Runtime {
  let r = runtimes.get(store);
  if (!r) { r = { epoch: 0, resolveConfirmation: null, requestSeq: new Map() }; runtimes.set(store, r); }
  return r;
}

export const useNutritionSelectionStore = defineStore("nutrition-selection", {
  state: (): State => ({
    session: null,
    status: "idle",
    pendingMode: null,
    pendingConfirmation: null,
    busy: false,
    message: null,
    compare: [],
  }),

  getters: {
    compareIds: (s): string[] => s.compare.map((c) => c.id),
    canCompare: (s): boolean => s.compare.length >= 2 && s.compare.length <= COMPARE_MAX,
  },

  actions: {
    /** Call synchronously on every buyer identity transition, before loading the next buyer. */
    resetForBuyer(): void {
      const r = runtime(this);
      r.epoch++;
      const cancel = r.resolveConfirmation;
      r.resolveConfirmation = null;
      r.requestSeq.clear();
      this.$reset();
      cancel?.(false);
    },

    adopt(next: PersonalSelectionSnapshot | PersonalSelectionWriteOk) {
      // Ответы записи не несут factsRevision — сохраняем последнее известное.
      this.session = { ...next, factsRevision: (next as PersonalSelectionSnapshot).factsRevision ?? this.session?.factsRevision };
    },

    async load(): Promise<void> {
      const r = runtime(this), epoch = r.epoch;
      this.status = "loading";
      try {
        const s = await personalSelectionApi.get();
        if (r.epoch !== epoch) return;
        if (!s || s.status !== "ok") throw new Error("bad snapshot");
        this.adopt(s);
        this.status = "ready";
      } catch (e) {
        if (r.epoch !== epoch) return;
        const code = selectionErrorCode(e);
        this.session = null;
        this.status = code === "feature_off" ? "off" : "error";
        this.message = code === "feature_off" ? null : code;
      }
    },

    async write(mode: PersonalSelectionMode, includeTraces: boolean): Promise<void> {
      if (!this.session || this.busy) return;
      const r = runtime(this), epoch = r.epoch;
      this.busy = true;
      this.pendingMode = mode;
      this.message = null;
      try {
        const next = await personalSelectionApi.setMode({ expectedRevision: this.session.revision, mode, includeTraces });
        if (r.epoch !== epoch) return;
        this.adopt(next);
      } catch (e) {
        if (r.epoch !== epoch) return;
        this.message = selectionErrorCode(e);
        // Конфликт CAS или сбой: берём актуальное состояние сервера, ничего не угадываем.
        try { const s = await personalSelectionApi.get(); if (r.epoch === epoch && s?.status === "ok") this.adopt(s); } catch { /* сохраняем прежнее */ }
      } finally {
        if (r.epoch === epoch) { this.pendingMode = null; this.busy = false; }
      }
    },

    async setMode(mode: PersonalSelectionMode): Promise<void> {
      if (!this.session || this.session.mode === mode) return;
      await this.write(mode, this.session.includeTraces);
    },

    async setIncludeTraces(value: boolean): Promise<void> {
      if (!this.session || this.session.mode === "ignore" || this.session.includeTraces === value) return;
      await this.write(this.session.mode, value);
    },

    /** Явное завершение сессии подбора: сервер возвращает режим по умолчанию. Профиль и корзина не меняются. */
    async endSession(): Promise<void> {
      if (this.busy) return;
      const r = runtime(this), epoch = r.epoch;
      this.busy = true;
      this.message = null;
      try { const next = await personalSelectionApi.reset(); if (r.epoch === epoch) this.adopt(next); }
      catch (e) { if (r.epoch === epoch) this.message = selectionErrorCode(e); }
      finally { if (r.epoch === epoch) this.busy = false; }
    },

    /**
     * Перед добавлением в корзину: серверное решение по этому SKU. confirmation_required →
     * открывается один диалог; «да» подтверждается сервером. Любой сбой — не «можно».
     * Корзина всё равно перепроверяет на своей стороне.
     */
    async checkBeforeAdd(itemId: string, title: string): Promise<CheckOutcome> {
      return (await this.evaluateForAdd(itemId, title)).outcome;
    },

    async evaluateForAdd(itemId: string, title: string): Promise<{ outcome: CheckOutcome; confirmed: boolean }> {
      const r = runtime(this), epoch = r.epoch;
      const done = (outcome: CheckOutcome, confirmed = false) => ({ outcome, confirmed });
      if (r.resolveConfirmation) { const prev = r.resolveConfirmation; r.resolveConfirmation = null; this.pendingConfirmation = null; prev(false); }
      this.message = null;
      let item;
      try {
        const response = await personalSelectionApi.evaluate([itemId]);
        if (r.epoch !== epoch) return done("cancelled");
        item = response.decisions.find((d) => d.itemId === itemId);
      } catch (e) {
        if (r.epoch !== epoch) return done("cancelled");
        const code = selectionErrorCode(e);
        if (code === "feature_off") { this.status = "off"; return done("feature_off"); }
        this.message = code;
        return done("error");
      }
      const d = item?.decision;
      if (!item || item.status !== "ok" || !d) { this.message = "unavailable"; return done("error"); }
      if (d.decision === "allow") return done("allow");
      if (d.decision === "block" || d.decision === "unknown_metadata") return done("blocked");
      if (d.decision !== "confirmation_required" || !d.confirmationKey
        || typeof item.generation !== "number" || typeof item.revision !== "number") {
        this.message = "unavailable";
        return done("error");
      }
      const pending: PendingConfirmation = {
        itemId, title, reasons: d.reasons ?? [], warningKey: d.confirmationKey,
        expectedGeneration: item.generation, expectedRevision: item.revision,
      };
      this.pendingConfirmation = pending;
      const answer = await new Promise<boolean>((resolve) => { r.resolveConfirmation = resolve; });
      if (r.epoch !== epoch) return done("cancelled");
      if (this.pendingConfirmation === pending) this.pendingConfirmation = null;
      if (!answer) return done("cancelled");
      try {
        const next = await personalSelectionApi.confirm({
          itemId, expectedGeneration: pending.expectedGeneration,
          expectedRevision: pending.expectedRevision, warningKey: pending.warningKey,
        });
        if (r.epoch !== epoch) return done("cancelled");
        this.adopt(next);
        return done("allow", true);
      } catch (e) {
        if (r.epoch !== epoch) return done("cancelled");
        const code = selectionErrorCode(e);
        this.message = code;
        return done(code === "conflict" || code === "stale" || code === "not_pending" ? "stale" : "error");
      }
    },

    /**
     * Корзина отклонила добавление (422 допуска). Ключ из тела корзины не используется:
     * решение и ключ заново берутся у сервера подбора. «allow» — можно один раз повторить добавление.
     */
    async recheckAfterCartRejection(_err: unknown, itemId: string, title: string): Promise<CheckOutcome> {
      const r = await this.evaluateForAdd(itemId, title);
      // Подбор разрешает без нового подтверждения, а корзина отказала — причина не в личном режиме: не повторяем.
      if (r.outcome === "allow" && !r.confirmed) return "blocked";
      return r.outcome;
    },

    async answerConfirmation(answer: boolean): Promise<void> {
      const state = runtime(this);
      const resolve = state.resolveConfirmation;
      state.resolveConfirmation = null;
      this.pendingConfirmation = null;
      resolve?.(answer);
      // Даём завершиться подтверждению на сервере до возврата управления UI.
      await Promise.resolve();
      await new Promise((res) => setTimeout(res, 0));
    },

    /** Защита от гонки: применяется только ответ на последний запрос с этим ключом. */
    async latest<T>(key: string, run: () => Promise<T>): Promise<{ fresh: true; value: T } | { fresh: false }> {
      const r = runtime(this), epoch = r.epoch;
      const id = (r.requestSeq.get(key) ?? 0) + 1;
      r.requestSeq.set(key, id);
      let value: T;
      try { value = await run(); }
      catch (e) { if (r.epoch !== epoch || r.requestSeq.get(key) !== id) return { fresh: false }; throw e; }
      return r.epoch === epoch && r.requestSeq.get(key) === id ? { fresh: true, value } : { fresh: false };
    },

    toggleCompare(entry: CompareEntry): CompareToggle {
      const i = this.compare.findIndex((c) => c.id === entry.id);
      if (i >= 0) { this.compare.splice(i, 1); return "removed"; }
      if (this.compare.length && this.compare[0].basisKey !== entry.basisKey) return "mixed_basis";
      if (this.compare.length >= COMPARE_MAX) return "full";
      this.compare.push({ ...entry });
      return "added";
    },

    clearCompare() { this.compare = []; },
  },
});
